import { useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown, Check, Copy } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { copyTableForExcel, parseAiAnswerSegments } from '@/lib/aiAnswerTables'
import { compareSortValues } from '@/lib/sortTable'

function sortCellValue(raw) {
  const text = String(raw ?? '').trim()
  if (!text || /^never$/i.test(text)) return null
  if (/^-?\d+(\.\d+)?$/.test(text)) return Number(text)
  // YYYY-MM-DD or YYYY-MM-DD HH:MM
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
    const ts = Date.parse(text.replace(' ', 'T'))
    if (!Number.isNaN(ts)) return ts
  }
  return text.toLowerCase()
}

function AiAnswerTable({ rows }) {
  const [copied, setCopied] = useState(false)
  const [sortCol, setSortCol] = useState(null)
  const [sortDir, setSortDir] = useState('asc')

  const header = rows?.[0] || []
  const body = useMemo(() => (rows?.length ? rows.slice(1) : []), [rows])

  const sortedBody = useMemo(() => {
    if (sortCol == null || !body.length) return body
    const dir = sortDir === 'desc' ? -1 : 1
    return [...body].sort((left, right) => {
      const cmp = compareSortValues(
        sortCellValue(left[sortCol]),
        sortCellValue(right[sortCol]),
      )
      return cmp * dir
    })
  }, [body, sortCol, sortDir])

  async function handleCopy() {
    try {
      await copyTableForExcel([header, ...sortedBody])
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  function handleSort(colIndex) {
    if (sortCol === colIndex) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortCol(colIndex)
      setSortDir('asc')
    }
  }

  if (!rows?.length) return null

  return (
    <div className="my-3 overflow-hidden rounded-lg border border-border bg-background">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/30 px-3 py-2">
        <p className="text-xs text-muted-foreground">
          Click a column header to sort. Copy pastes the current order into Excel.
        </p>
        <Button type="button" size="sm" variant="outline" className="h-8 gap-1.5" onClick={handleCopy}>
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Copy for Excel'}
        </Button>
      </div>
      <div className="max-h-[28rem] overflow-auto">
        <table className="w-full min-w-[32rem] border-collapse text-left text-xs">
          <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm">
            <tr>
              {header.map((cell, index) => {
                const active = sortCol === index
                return (
                  <th
                    key={`h-${index}`}
                    className="cursor-pointer select-none border-b border-border px-3 py-2 font-medium whitespace-nowrap hover:text-foreground"
                    onClick={() => handleSort(index)}
                    title={`Sort by ${cell}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {cell}
                      {active ? (
                        sortDir === 'asc' ? (
                          <ArrowUp className="h-3.5 w-3.5 shrink-0" />
                        ) : (
                          <ArrowDown className="h-3.5 w-3.5 shrink-0" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3.5 w-3.5 shrink-0 opacity-40" />
                      )}
                    </span>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {sortedBody.map((row, rowIndex) => (
              <tr key={`r-${rowIndex}`} className="odd:bg-muted/15">
                {row.map((cell, cellIndex) => (
                  <td
                    key={`c-${rowIndex}-${cellIndex}`}
                    className="border-b border-border/60 px-3 py-1.5 align-top"
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function AiAnswer({ answer }) {
  const segments = useMemo(() => parseAiAnswerSegments(answer), [answer])

  if (!answer) return null

  if (!segments.length) {
    return (
      <div className="min-h-[12rem] flex-1 rounded-lg border border-border bg-muted/20 p-4 text-sm whitespace-pre-wrap">
        {answer}
      </div>
    )
  }

  return (
    <div className="min-h-[12rem] flex-1 rounded-lg border border-border bg-muted/20 p-4 text-sm">
      {segments.map((segment, index) => {
        if (segment.type === 'table') {
          return <AiAnswerTable key={`table-${index}`} rows={segment.rows} />
        }
        return (
          <div key={`prose-${index}`} className="whitespace-pre-wrap">
            {segment.text}
          </div>
        )
      })}
    </div>
  )
}
